import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useResponsiveLayout } from "@/hooks/useResponsiveLayout";

interface AdaptiveGridProps {
  children: ReactNode;
  className?: string;
  minItemWidth?: string;
  gap?: "sm" | "md" | "lg";
  adaptiveColumns?: {
    mobile?: number;
    tablet?: number;
    laptop?: number;
    desktop?: number;
  };
}

const gapClasses = {
  sm: "gap-2 sm:gap-3",
  md: "gap-3 sm:gap-4 lg:gap-6",
  lg: "gap-4 sm:gap-6 lg:gap-8"
};

export const AdaptiveGrid = ({
  children,
  className,
  minItemWidth = "280px",
  gap = "md",
  adaptiveColumns
}: AdaptiveGridProps) => {
  const { screenSize } = useResponsiveLayout();

  const getGridColumns = () => {
    if (adaptiveColumns) {
      switch (screenSize) {
        case 'mobile':
          return `grid-cols-${adaptiveColumns.mobile || 1}`;
        case 'tablet':
          return `grid-cols-${adaptiveColumns.tablet || 2}`;
        case 'laptop':
          return `grid-cols-${adaptiveColumns.laptop || 3}`;
        case 'desktop':
        case 'large-desktop':
          return `grid-cols-${adaptiveColumns.desktop || 4}`;
        default:
          return 'grid-cols-1';
      }
    }

    // Auto-fit based on min width
    return `grid-cols-[repeat(auto-fit,minmax(${minItemWidth},1fr))]`;
  };

  return (
    <div className={cn(
      "grid w-full",
      getGridColumns(),
      gapClasses[gap],
      className
    )}>
      {children}
    </div>
  );
};